/**
 * Money formatting for Saudi riyal amounts.
 *
 * Amounts are integer minor units (halalas) everywhere upstream. Dividing
 * is the last thing that happens to a number, and it happens here. No
 * imports on purpose: the file is drop-in for a bundle that cannot take a
 * workspace dependency.
 */

export type Locale = "ar-SA" | "en-SA" | "en-US";

/**
 * Map a ui language tag onto the numeral locale.
 *
 * The rule keys off the DIGIT SYSTEM, not the ui language: only Arabic
 * gets Arabic-Indic, so fr/tr read Western digits exactly like en. This
 * lives here because every surface that formats a number needs it, and
 * three hand-rolled copies had already drifted apart.
 */
export function numeralLocale(locale: string): Locale {
  return locale.startsWith("ar") ? "ar-SA" : "en-US";
}

/** U+20C1 SAUDI RIYAL SIGN (Unicode 17.0, September 2025). */
export const RIYAL = "\u20C1";

/** LTR isolate marks. Wrap any digit run embedded in RTL prose. */
const LRI = "\u2066";
const PDI = "\u2069";

const TWO_DECIMALS: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

/**
 * Which digit system a price renders in.
 *
 * "localised" is the doctrine (TOKENS.md, rule 6: values localise).
 * "western" is the escape hatch for a shopper-facing surface whose own rule
 * is Latin digits everywhere (every surveyed Saudi store prices in 0123),
 * where a price in Arabic-Indic beside a "50 مل" spec line would be the
 * mixed-system tell.
 */
export type PriceNumerals = "localised" | "western";

export interface PriceOptions {
  /** Any ui language tag ("ar", "en-SA", "fr"): only Arabic localises. */
  locale?: string;
  /** ISO 4217. SAR gets the riyal sign; anything else uses Intl's symbol. */
  currency?: string;
  numerals?: PriceNumerals;
  /** Drop the sign, e.g. under a column header that already carries it. */
  symbol?: boolean;
}

/**
 * A price with the riyal sign.
 *
 * SAMA guidance puts the symbol to the LEFT of the number with a space,
 * in BOTH text directions: it does not mirror in RTL, which is the
 * opposite of what the bidi algorithm would do unaided. Hence the
 * isolate wrapper.
 *
 * The symbol is applied manually because CLDR 48 still lists U+20C1 as
 * an *alternative* currency symbol, so Intl with currency:'SAR' returns
 * ر.س or SAR instead.
 */
export function formatPrice(minor: number, options: PriceOptions = {}): string {
  const {
    locale = "ar-SA",
    currency = "SAR",
    numerals = "localised",
    symbol = true,
  } = options;
  const base = numeralLocale(locale);
  const tag = numerals === "western" ? `${base}-u-nu-latn` : base;
  const amount = minor / 100;
  if (!symbol) return new Intl.NumberFormat(tag, TWO_DECIMALS).format(amount);
  if (currency !== "SAR") {
    const n = new Intl.NumberFormat(tag, {
      style: "currency",
      currency,
    }).format(amount);
    return `${LRI}${n}${PDI}`;
  }
  const n = new Intl.NumberFormat(tag, TWO_DECIMALS).format(amount);
  return `${LRI}${RIYAL} ${n}${PDI}`;
}

/**
 * Digits only: "1234.50". Western digits, no grouping, no currency, no
 * isolate. For anything a machine parses (CSV cells, API payloads, model
 * tool summaries) rather than a person reads. Integer arithmetic so a
 * large halala count never picks up float noise on the way to text.
 */
export function formatMinor(minor: number): string {
  const rounded = Math.round(minor);
  const abs = Math.abs(rounded);
  const body = `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
  return rounded < 0 ? `-${body}` : body;
}
